import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent, ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';
import { IconButton } from '@/shared/ui/Button';
import { CloseIcon } from '@/shared/ui/icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/*
 * The scroll lock is shared, so it is counted rather than owned per instance.
 *
 * Each modal used to save and restore `document.body.style.overflow` itself,
 * which is right for one modal and wrong for two. Open A, open B, then close A
 * first: A restores the '' it captured while B is still on screen, so the page
 * scrolls behind the dialog — and when B closes it restores the 'hidden' *it*
 * captured, leaving the page unscrollable with no modal to explain why and no
 * way back short of a reload.
 *
 * Counting means the first lock stores the original value and only the last
 * release puts it back, whatever order they close in.
 */
let scrollLocks = 0;
let overflowBeforeLock = '';

function lockScroll() {
  if (scrollLocks === 0) {
    overflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks += 1;
}

function releaseScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = overflowBeforeLock;
}

export type ModalProps = {
  open: boolean;
  /** Omit to make the modal non-dismissable — used for blocking states. */
  onClose?: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Buttons for the footer, in DOM order. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children?: ReactNode;
};

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  className,
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  // Remember what had focus so it can be handed back on close; without this,
  // dismissing a modal drops the user at the top of the page.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;

    const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panelRef.current)?.focus();

    return () => restoreRef.current?.focus?.();
  }, [open]);

  // A modal that leaves the page scrolling behind it lets the user lose it.
  useEffect(() => {
    if (!open) return;
    lockScroll();
    return releaseScroll;
  }, [open]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && onClose) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap: wrap at both ends so Tab cannot escape into the page behind.
      const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <div className="modal__overlay" onKeyDown={handleKeyDown}>
      {/* The backdrop only dismisses when the modal is dismissable at all. */}
      <div
        className="modal__scrim"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        className={cx('modal', `modal--${size}`, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
      >
        <div className="modal__head">
          <div className="modal__head-text">
            <h3 className="modal__title" id={titleId}>
              {title}
            </h3>
            {description ? (
              <p className="modal__desc" id={descId}>
                {description}
              </p>
            ) : null}
          </div>
          {onClose ? (
            /* "Close dialog", not "Close" — dialogs often carry a footer button
               already named Close or Cancel, and two controls with the same
               accessible name doing the same thing is ambiguous to anyone
               navigating by name. */
            <IconButton label="Close dialog" size="sm" onClick={onClose} className="modal__close">
              <CloseIcon size={18} />
            </IconButton>
          ) : null}
        </div>

        {children ? <div className="modal__body">{children}</div> : null}
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
